import { OptimizelyContext, OptimizelyDecision, useDecision } from '@optimizely/react-sdk';
import { useContext } from 'react';

import { frontendAnalyticsTrack } from 'analytics/utils';

import optimizely from '.';
import { Flags } from './types';

const TRACKED_FEATURE_FLAGS: Flags[] = [
  // Flags.HOUSEHOLD_INCOME_UNDER_125K,
  // Flags.HOUSEHOLD_INCOME_OVER_125K,
  Flags.INDIVIDUAL_INCOME_OVER_125K,
  Flags.PROGRESSIVE_OFFBOARDING,
  Flags.SHORTENED_MEDICAL_CONDITIONS_PAGE,
  Flags.COVERAGE_GOALS_4_TILE,
];

const TRACKED_FLAGS_SESSION_KEY = 'optimizely_campaign';

const isSessionStorageAvailable = (): boolean => {
  try {
    return typeof sessionStorage !== 'undefined';
  } catch {
    return false;
  }
};

const getTrackedFlags = (): Set<string> => {
  if (!isSessionStorageAvailable()) {
    return new Set();
  }

  try {
    const stored = sessionStorage.getItem(TRACKED_FLAGS_SESSION_KEY);

    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const markFlagAsTracked = (trackedFlags: Set<string>, flagKey: string): void => {
  if (!isSessionStorageAvailable()) {
    return;
  }

  try {
    trackedFlags.add(flagKey);
    sessionStorage.setItem(TRACKED_FLAGS_SESSION_KEY, JSON.stringify(Array.from(trackedFlags)));
  } catch {
    // Ignore errors
  }
};

const trackFeatureFlagViewed = (decision: OptimizelyDecision): void => {
  const trackedFlags = getTrackedFlags();

  if (!trackedFlags.has(decision.flagKey)) {
    frontendAnalyticsTrack('Feature Experiment Viewed', {
      flagKey: decision.flagKey,
      variationKey: decision.variationKey,
      featureEnabled: decision.enabled,
      ruleKey: decision.ruleKey,
      variables: decision.variables,
    });
    markFlagAsTracked(trackedFlags, decision.flagKey);
  }
};

export const useFeatureExperiment = (flagKey: Flags, eligibleForExperiment = true) => {
  const ctx = useContext(OptimizelyContext);
  const [decision] = useDecision(flagKey, undefined, {
    overrideAttributes: {
      ...ctx.optimizely?.user.attributes,
      eligible_for_experiment: eligibleForExperiment,
    },
  });

  return decision;
};

export const getFeatureExperiment = (
  flagKey: Flags,
  eligibleForExperiment = true,
  otherAttributes = {}
) => {
  const decision = optimizely.decide(flagKey, undefined, undefined, {
    ...optimizely.user.attributes,
    eligible_for_experiment: eligibleForExperiment,
    ...otherAttributes,
  });

  // Track feature flags that don't dispatch decision events automatically
  // Uses session storage to ensure we only track once per flag per session
  if (TRACKED_FEATURE_FLAGS.includes(flagKey) && eligibleForExperiment) {
    trackFeatureFlagViewed(decision);
  }

  return decision;
};
